'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import toast from 'react-hot-toast'
import { CATEGORIES } from '@/lib/categories'

// Quick-start vendor registration. Only fields strictly needed to create
// the account live on this page. Business identity (legal name, BR cert,
// NIC, etc.) is collected later from /dashboard/business — completing
// that flow earns the "Verified" badge. Email verification is mandatory
// before sign-in works.

type SessionInfo = { email: string; name: string | null } | null

type SellerType = 'business' | 'individual'

type FormState = {
  sellerType: SellerType
  email: string
  password: string
  contactName: string
  contactMobile: string
  address: string
  city: string
  isIslandWide: boolean
  categories: string[]
  agreedToTerms: boolean
}

const INITIAL: FormState = {
  sellerType: 'business',
  email: '',
  password: '',
  contactName: '',
  contactMobile: '',
  address: '',
  city: '',
  isIslandWide: true,
  categories: [],
  agreedToTerms: false,
}

export default function RegisterPage() {
  const router = useRouter()
  const [form, setForm] = useState<FormState>(INITIAL)
  const [session, setSession] = useState<SessionInfo>(null)
  const [submitting, setSubmitting] = useState(false)
  const [emailExists, setEmailExists] = useState(false)
  const [submitted, setSubmitted] = useState<{
    email: string
    requiresEmailVerification: boolean
    verifyUrl?: string
  } | null>(null)

  useEffect(() => {
    fetch('/api/auth/session')
      .then((r) => r.json())
      .then((s: { user?: { email?: string; name?: string | null } }) => {
        if (s?.user?.email) {
          setSession({ email: s.user.email, name: s.user.name ?? null })
          setForm((f) => ({
            ...f,
            contactName: f.contactName || s.user!.name || '',
          }))
        }
      })
      .catch(() => {})
  }, [])

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }))
    if (k === 'email') setEmailExists(false)
  }

  function toggleCategory(value: string) {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(value)
        ? f.categories.filter((c) => c !== value)
        : [...f.categories, value],
    }))
  }

  function validate(): string | null {
    if (!session) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
        return 'Valid email required'
      if (form.password.length < 8) return 'Password must be at least 8 characters'
    }
    if (!form.contactName.trim()) return 'Your name is required'
    if (!/^[+\d\s\-()]{7,20}$/.test(form.contactMobile))
      return 'Valid mobile number required'
    if (form.sellerType === 'individual' && !form.address.trim())
      return 'Address is required'
    if (!form.isIslandWide && !form.city.trim())
      return 'Provide a city or tick island-wide'
    if (!form.agreedToTerms) return 'Please accept the terms to continue'
    return null
  }

  async function submit() {
    const err = validate()
    if (err) {
      toast.error(err)
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch('/api/v1/vendor/register', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...form,
          // Individuals are stored as a Vendor with businessType='individual';
          // businesses leave it unset and fill business identity later.
          businessType: form.sellerType === 'individual' ? 'individual' : undefined,
        }),
      })
      const json = await res.json()
      if (!res.ok) {
        if (res.status === 409 && json.code === 'email_exists') {
          setEmailExists(true)
        } else if (res.status === 409 && json.code === 'vendor_exists') {
          toast.error('You already have a vendor profile — opening dashboard')
          router.push('/dashboard')
          return
        }
        toast.error(json.error ?? 'Registration failed')
        return
      }
      setSubmitted({
        email: form.email || session?.email || '',
        requiresEmailVerification: !!json.requiresEmailVerification,
        verifyUrl: json.verifyUrl,
      })
    } catch {
      toast.error('Network error — please try again')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <main className="min-h-screen bg-navy text-text-1 flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full bg-navy-2 border border-[var(--bdr)] rounded-md p-8 text-center">
          {submitted.requiresEmailVerification ? (
            <>
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-cyan/15 border border-cyan/40 flex items-center justify-center text-2xl text-cyan">
                ✉
              </div>
              <h1 className="text-xl font-bold mb-1">Check your email</h1>
              <p className="text-text-2 text-sm">
                We sent a verification link to{' '}
                <span className="text-text-1 font-semibold">{submitted.email}</span>.
                Click it to activate your account.
              </p>
              {submitted.verifyUrl && (
                <p className="text-[11px] text-amber mt-3 break-all">
                  Dev mode (no email service configured):{' '}
                  <a href={submitted.verifyUrl} className="underline">
                    open the verification link
                  </a>
                </p>
              )}
              <p className="text-[11px] text-text-3 mt-5">
                Didn&apos;t get the email?{' '}
                <Link
                  href={`/auth/verify?email=${encodeURIComponent(submitted.email)}`}
                  className="text-cyan hover:underline"
                >
                  Resend →
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green/15 border border-green/40 flex items-center justify-center text-2xl text-green">
                ✓
              </div>
              <h1 className="text-xl font-bold mb-1">Vendor profile created</h1>
              <p className="text-text-2 text-sm">
                You can start posting listings from your dashboard now.
              </p>
              <Link
                href="/dashboard"
                className="inline-block mt-5 px-5 py-2 bg-white text-navy font-semibold rounded hover:brightness-95"
              >
                Open dashboard →
              </Link>
            </>
          )}
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-navy text-text-1 px-4 py-10">
      <div className="max-w-md mx-auto">
        <Link href="/" className="inline-flex items-center gap-2 mb-6 text-text-2 hover:text-text-1 text-sm">
          <span>←</span>
          <span>VOLT<span className="text-cyan">CHARGE</span></span>
        </Link>

        <h1 className="text-2xl font-bold">Start selling on VoltCharge Marketplace</h1>
        <p className="text-text-2 text-sm mt-2 mb-5">
          {form.sellerType === 'individual' ? (
            <>
              List your used EV or other items in about a minute — just your
              contact details, no business registration needed.
            </>
          ) : (
            <>
              Takes 60 seconds. You can list anything immediately. Complete your
              business details later to earn the <VerifiedInline /> badge that
              buyers trust.
            </>
          )}
        </p>

        {session && (
          <div className="mb-4 bg-cyan/10 border border-cyan/40 rounded-md p-3 text-xs">
            Signed in as <span className="font-semibold text-text-1">{session.email}</span>
            {' — '}your vendor profile will attach to this account.
          </div>
        )}

        <div className="bg-navy-2 border border-[var(--bdr)] rounded-md p-6 space-y-4">
          <div>
            <label className="block text-xs text-text-3 mb-1.5">I&apos;m registering as</label>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['business', '🏢', 'Business', 'Dealer, shop or service'],
                  ['individual', '👤', 'Individual', 'Selling my own vehicle/items'],
                ] as const
              ).map(([val, emoji, label, sub]) => {
                const on = form.sellerType === val
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => update('sellerType', val)}
                    className={[
                      'px-3 py-2.5 rounded border text-left transition-colors',
                      on
                        ? 'bg-cyan/15 border-cyan/40 text-cyan'
                        : 'bg-navy-3 border-[var(--bdr)] text-text-2 hover:text-text-1',
                    ].join(' ')}
                  >
                    <div className="text-sm font-semibold">{emoji} {label}</div>
                    <div className="text-[10px] text-text-3">{sub}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {!session && (
            <>
              <Field label="Email" required>
                <input
                  type="email"
                  autoComplete="email"
                  className={[
                    inputCls,
                    emailExists ? 'border-red/60 focus:border-red' : '',
                  ].join(' ')}
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                />
                {emailExists && (
                  <p className="text-[11px] text-red mt-1">
                    Already registered.{' '}
                    <Link
                      href={`/auth/signin?callbackUrl=${encodeURIComponent('/register')}`}
                      className="underline font-semibold"
                    >
                      Sign in instead →
                    </Link>
                  </p>
                )}
              </Field>
              <Field label="Password" required>
                <input
                  type="password"
                  autoComplete="new-password"
                  className={inputCls}
                  placeholder="Min 8 characters"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                />
              </Field>
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Your name" required>
              <input
                className={inputCls}
                placeholder="Full name"
                value={form.contactName}
                onChange={(e) => update('contactName', e.target.value)}
              />
            </Field>
            <Field label="Mobile" required>
              <input
                className={inputCls}
                placeholder="+94 77 123 4567"
                value={form.contactMobile}
                onChange={(e) => update('contactMobile', e.target.value)}
              />
            </Field>
          </div>

          <Field label="What do you sell? (optional, pick later)">
            <div className="grid grid-cols-2 gap-1.5">
              {CATEGORIES.map((c) => {
                const on = form.categories.includes(c.value)
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => toggleCategory(c.value)}
                    className={[
                      'px-2 py-1.5 text-xs rounded border text-left transition-colors',
                      on
                        ? 'bg-cyan/15 border-cyan/40 text-cyan'
                        : 'bg-navy-3 border-[var(--bdr)] text-text-2 hover:text-text-1',
                    ].join(' ')}
                  >
                    {c.emoji} {c.label}
                  </button>
                )
              })}
            </div>
          </Field>

          <div>
            <label className="inline-flex items-center gap-2 text-xs text-text-2">
              <input
                type="checkbox"
                checked={form.isIslandWide}
                onChange={(e) => update('isIslandWide', e.target.checked)}
              />
              I serve customers island-wide
            </label>
            {!form.isIslandWide && (
              <input
                className={`${inputCls} mt-2`}
                placeholder="Primary city"
                value={form.city}
                onChange={(e) => update('city', e.target.value)}
              />
            )}
          </div>

          <Field
            label={form.sellerType === 'individual' ? 'Address' : 'Address (optional)'}
            required={form.sellerType === 'individual'}
          >
            <input
              className={inputCls}
              placeholder="Street / area, city"
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
            />
          </Field>

          <label className="flex items-start gap-2 text-xs text-text-2 pt-1">
            <input
              type="checkbox"
              checked={form.agreedToTerms}
              onChange={(e) => update('agreedToTerms', e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I agree to the{' '}
              <Link href="/legal/vendor-terms" className="text-cyan hover:underline">
                Vendor Agreement
              </Link>{' '}
              and Listing Guidelines.
            </span>
          </label>

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full mt-2 px-5 py-2.5 text-sm font-semibold bg-white text-navy rounded hover:brightness-95 disabled:opacity-50"
          >
            {submitting ? 'Creating account…' : 'Create my vendor account →'}
          </button>
        </div>

        <p className="text-center text-text-3 text-xs mt-4">
          Already registered?{' '}
          <Link href="/auth/signin" className="text-cyan hover:underline">
            Sign in →
          </Link>
        </p>
      </div>
    </main>
  )
}

const inputCls =
  'w-full px-3 py-2 bg-navy-3 border border-[var(--bdr)] rounded text-sm text-text-1 placeholder:text-text-3 focus:outline-none focus:border-cyan/50'

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-text-2 mb-1">
        {label}
        {required && <span className="text-red ml-1">*</span>}
      </label>
      {children}
    </div>
  )
}

function VerifiedInline() {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green/15 border border-green/40 text-green text-[10px] font-mono uppercase">
      ✓ Verified
    </span>
  )
}
